/**
 * Stalwart Prints: cart arithmetic.
 *
 * Pure functions, no DOM, no network. cart.js does the talking to Shopify;
 * this file decides WHAT to send. Kept separate so the rules that protect the
 * print charge can be unit-tested (tests/cart-logic.test.mjs) without a browser.
 *
 * A designed shirt with a print area is two cart lines: the shirt, carrying
 * `_design_id` and `_print_units`, and a `print-charge` line with the same
 * `_design_id` whose quantity must equal the SUM over every shirt of that design
 * of quantity x `_print_units` (two colours of one saved design share one merged
 * print line). Shopify prices both lines; the browser only decides quantities.
 *
 * Lines come from the JSON block that snippets/cart-pairing-state.liquid
 * renders, never from cart.js alone: product tags (which mark the print
 * product) are not in the cart JSON.
 *
 *   { key, qty, variantId, designId, units, isPrint }
 *
 * `units` is the raw `_print_units` string, or null when the line has none.
 * `isPrint` is true only for a `print-charge` line WITH a design id (paired).
 */

(function (root) {
  'use strict';

  const isCount = (value) =>
    value !== null && value !== undefined && /^\d+$/.test(String(value).trim()) && Number.isSafeInteger(Number(value));

  // A shirt that pays for print: has a design id and a `_print_units` value.
  // A malformed `_print_units` still marks it as printed; its total is unknown.
  const isPrintedShirt = (line) => !line.isPrint && !!line.designId && line.units !== null && line.units !== undefined;

  /**
   * Print quantity each design needs: designId -> total, or null when any of
   * its shirts cannot be read (then the design is left alone, never guessed).
   * `quantities` overrides line quantities by key (pending or planned changes).
   */
  function expectedPrintQuantities(lines, quantities = new Map()) {
    const totals = new Map();
    for (const line of lines) {
      if (!isPrintedShirt(line)) continue;
      const id = line.designId;
      if (totals.get(id) === null) continue;
      const qty = quantities.has(line.key) ? quantities.get(line.key) : line.qty;
      if (!isCount(qty) || !isCount(line.units)) {
        totals.set(id, null);
        continue;
      }
      const total = (totals.get(id) || 0) + Number(qty) * Number(line.units);
      totals.set(id, Number.isSafeInteger(total) ? total : null);
    }
    return totals;
  }

  const printLinesFor = (lines, designId) => lines.filter((line) => line.isPrint && line.designId === designId);

  /**
   * The ONE update that applies `changes` (key -> new quantity) and keeps every
   * affected print line paired. Removing a shirt is a change to 0: its print
   * line drops to what the REMAINING shirts of the design need, zero only when
   * none remain, because another colour may share that line.
   *
   * When a design has more than one print line (unusual: Shopify merges lines
   * with identical properties), the first carries the whole charge and the
   * rest go to zero, so the print is never charged twice.
   *
   * Returns { updates, unknown }: `unknown` lists design ids whose print could
   * not be computed (their print lines are left untouched). The shirt change is
   * still sent, so the customer can always edit or remove such a line; holdState
   * keeps checkout blocked while it is there.
   */
  function planChanges(lines, changes) {
    const quantities = new Map();
    const updates = {};
    for (const [key, qty] of changes) {
      if (!isCount(qty)) continue;
      const line = lines.find((l) => l.key === key);
      // Print lines follow their shirts; they are never changed directly.
      if (!line || line.isPrint) continue;
      quantities.set(key, Number(qty));
      updates[key] = Number(qty);
    }

    const affected = new Set();
    for (const key of quantities.keys()) {
      const line = lines.find((l) => l.key === key);
      if (isPrintedShirt(line)) affected.add(line.designId);
    }

    const unknown = [];
    const want = expectedPrintQuantities(lines, quantities);
    for (const designId of affected) {
      const target = want.has(designId) ? want.get(designId) : 0;
      if (target === null) {
        unknown.push(designId);
        continue;
      }
      printLinesFor(lines, designId).forEach((printLine, index) => {
        updates[printLine.key] = index === 0 ? target : 0;
      });
    }
    return { updates, unknown };
  }

  /**
   * Fixes for a cart that drifted: print lines whose shirt is gone go to zero,
   * and print lines whose quantity no longer matches their shirts are reset
   * (for example after a no-JavaScript Update that changed only the shirt).
   * `short` is true when any print line is BELOW what its shirts need: checking
   * out as it stands would undercharge.
   */
  function planOrphanFixes(lines) {
    const updates = {};
    let short = false;
    const want = expectedPrintQuantities(lines);
    const seen = new Set();
    for (const line of lines) {
      if (!line.isPrint || !line.designId) continue;
      const first = !seen.has(line.designId);
      seen.add(line.designId);
      const target = want.has(line.designId) ? want.get(line.designId) : 0;
      if (target === null) continue;
      const wanted = first ? target : 0;
      if (line.qty !== wanted) {
        updates[line.key] = wanted;
        if (!isCount(line.qty) || Number(line.qty) < wanted) short = true;
      }
    }
    return { updates, short };
  }

  /**
   * Whether checkout must be held, from the lines alone:
   * - missing: a shirt with print units > 0 has no print line at all (the cart
   *   cannot re-add it: it has no variant id for the charge);
   * - short: a print line holds less than its shirts need;
   * - unreadable: a printed shirt's `_print_units` is not a whole number, so what
   *   it should pay for print cannot be known.
   */
  function holdState(lines) {
    let missing = false;
    for (const line of lines) {
      if (!isPrintedShirt(line) || !isCount(line.units) || Number(line.units) === 0) continue;
      if (printLinesFor(lines, line.designId).length === 0) missing = true;
    }
    let short = false;
    const want = expectedPrintQuantities(lines);
    for (const [designId, target] of want) {
      if (target === null || target === 0) continue;
      const have = printLinesFor(lines, designId).reduce((sum, l) => sum + (Number(l.qty) || 0), 0);
      if (printLinesFor(lines, designId).length > 0 && have < target) short = true;
    }
    const unreadable = lines.some((line) => isPrintedShirt(line) && !isCount(line.units));
    return { missing, short, unreadable, held: missing || short || unreadable };
  }

  /* --------------------------------------------------------------------
   * Verifying an add (the same rules as theme.js uses for the product page)
   *
   * Verifying an add by re-reading the cart, not by trusting the add response.
   * The same saved design added in two colours gives two shirt lines with one
   * `_design_id`, and their print lines have identical properties, so Shopify
   * merges them into ONE print line. Whether /cart/add.js then reports the
   * quantity just added or the merged line's total is unconfirmed, so comparing
   * the response to the request could roll back a good add or pass a short one.
   * Instead: snapshot every line before the add, read the cart again after it,
   * and require each requested (variant, design id) group to have grown by
   * exactly the requested quantity.
   * ------------------------------------------------------------------ */

  // key -> { quantity, group } for every line of a cart.js response, where
  // group is "<variant id>|<design id>" (design id empty without one).
  function snapshotLines(cart) {
    const lines = new Map();
    for (const line of cart.items) {
      const designId = line.properties && line.properties._design_id != null ? String(line.properties._design_id) : '';
      lines.set(line.key, {
        quantity: Number(line.quantity) || 0,
        group: `${String(line.variant_id)}|${designId}`,
      });
    }
    return lines;
  }

  function groupTotals(lines) {
    const totals = new Map();
    for (const { quantity, group } of lines.values()) totals.set(group, (totals.get(group) || 0) + quantity);
    return totals;
  }

  // True only when every requested (variant, design id) group holds exactly
  // its snapshot quantity plus what was requested. Shopify can add fewer than
  // asked (stock) and report it only in a message, so a short add fails here.
  function addVerified(before, after, requested, designId) {
    const want = new Map();
    for (const item of requested) {
      const group = `${String(item.id)}|${designId}`;
      want.set(group, (want.get(group) || 0) + item.quantity);
    }
    const had = groupTotals(before);
    const has = groupTotals(after);
    for (const [group, quantity] of want) {
      if ((has.get(group) || 0) !== (had.get(group) || 0) + quantity) return false;
    }
    return true;
  }

  // The update that puts the cart back exactly as the snapshot had it: lines
  // present before go back to their old quantity, lines the add created go to
  // zero. Earlier adds of the same design (another colour) are therefore kept.
  function restoreUpdates(before, after) {
    const updates = {};
    for (const [key, line] of after) {
      const target = before.has(key) ? before.get(key).quantity : 0;
      if (line.quantity !== target) updates[key] = target;
    }
    return updates;
  }

  /**
   * Re-adding a line the customer removed, from the cart.js line captured
   * before removal. A designed shirt brings its print back as units x qty: NOT
   * the old print line's quantity, which may also have paid for another colour
   * that is still in the cart. Properties are copied whole, so the print line
   * merges back into an existing one when it still exists.
   *
   * Returns { designId, items } for /cart/add.js, or null when the line cannot
   * be rebuilt safely (then Undo is not offered).
   */
  function planUndo(shirt, printLine) {
    if (!shirt || !isCount(shirt.variant_id) || !isCount(shirt.quantity) || Number(shirt.quantity) < 1) return null;
    const properties = { ...(shirt.properties || {}) };
    const item = { id: Number(shirt.variant_id), quantity: Number(shirt.quantity), properties };
    if (shirt.selling_plan_allocation?.selling_plan?.id) {
      item.selling_plan = shirt.selling_plan_allocation.selling_plan.id;
    }
    const designId = properties._design_id != null ? String(properties._design_id) : '';
    const unitsRaw = properties._print_units;
    const printed = designId && unitsRaw !== undefined && unitsRaw !== null && String(unitsRaw) !== '';
    if (!printed) return { designId, items: [item] };

    if (!isCount(unitsRaw)) return null;
    const units = Number(unitsRaw);
    if (units === 0) return { designId, items: [item] };
    if (!printLine || !isCount(printLine.variant_id)) return null;
    const printQuantity = units * item.quantity;
    if (!Number.isSafeInteger(printQuantity)) return null;
    return {
      designId,
      items: [
        item,
        { id: Number(printLine.variant_id), quantity: printQuantity, properties: { ...(printLine.properties || {}) } },
      ],
    };
  }

  /* --------------------------------------------------------------------
   * One request in flight, ever
   *
   * Cart quantities are absolute, so two requests in flight can land in either
   * order, and an aborted fetch may still reach Shopify. Every job therefore
   * runs strictly after the one before it. Jobs that are queued but not yet
   * started can be merged: `defer(key, value)` collects quantity changes and
   * hands the whole batch to ONE job, built when it starts, so it is planned
   * against the newest cart rather than the one on screen at click time.
   * ------------------------------------------------------------------ */

  function createQueue() {
    let tail = Promise.resolve();
    let size = 0;
    const idleWaiters = [];
    let pending = null; // Map of deferred changes awaiting their job

    const settle = () => {
      size -= 1;
      if (size === 0) idleWaiters.splice(0).forEach((resolve) => resolve());
    };

    // Runs `job` after every earlier job; resolves or rejects with its result.
    function run(job) {
      size += 1;
      const result = tail.then(() => job());
      tail = result.then(settle, settle);
      return result;
    }

    // Queues a change. All changes deferred before their job starts are given
    // to `send(changes)` together, once.
    function defer(key, value, send) {
      if (pending) {
        pending.set(key, value);
        return pending.result;
      }
      const batch = new Map([[key, value]]);
      pending = batch;
      batch.result = run(() => {
        if (pending === batch) pending = null;
        return send(batch);
      });
      return batch.result;
    }

    return {
      run,
      defer,
      get busy() {
        return size > 0;
      },
      idle() {
        return size === 0 ? Promise.resolve() : new Promise((resolve) => idleWaiters.push(resolve));
      },
    };
  }

  root.StalwartCart = Object.freeze({
    isCount,
    expectedPrintQuantities,
    planChanges,
    planOrphanFixes,
    holdState,
    snapshotLines,
    groupTotals,
    addVerified,
    restoreUpdates,
    planUndo,
    createQueue,
  });
})(globalThis);
